import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { CheckCircle2, Mail, Send, ShieldCheck } from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  "Consumer Packaged Goods (CPG)",
  "Food & Beverage",
  "Pharmaceuticals & Life Sciences",
  "Automotive",
  "Aerospace & Defense",
  "Industrial Manufacturing",
  "Chemicals",
  "Oil, Gas & Energy",
  "Utilities",
  "Mining & Metals",
  "Banking",
  "Insurance",
  "Capital Markets",
  "Retail & eCommerce",
  "Technology & Software",
  "Telecommunications",
  "Healthcare Providers",
  "Logistics & Transportation",
  "Construction & Engineering",
  "Media & Entertainment",
  "Other",
];

const SPEND_RANGES = [
  "Under $10M",
  "$10M – $50M",
  "$50M – $250M",
  "$250M – $1B",
  "$1B – $5B",
  "Over $5B",
];

const INITIAL = {
  company_name: "",
  industry: "",
  industry_other: "",
  first_name: "",
  last_name: "",
  business_email: "",
  annual_indirect_spend: "",
  problem_statement: "",
};

export default function LeadForm() {
  const { t } = useLocale();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [errors, setErrors] = useState({});

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.company_name.trim()) next.company_name = "Required";
    if (!form.industry) next.industry = "Select an industry";
    if (form.industry === "Other" && !form.industry_other.trim())
      next.industry_other = "Please specify your industry";
    if (!form.first_name.trim()) next.first_name = "Required";
    if (!form.last_name.trim()) next.last_name = "Required";
    if (!form.business_email.trim()) next.business_email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.business_email))
      next.business_email = "Enter a valid business email";
    if (!form.annual_indirect_spend) next.annual_indirect_spend = "Select a range";
    if (!form.problem_statement.trim())
      next.problem_statement = "Tell us the problem you want to solve";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t("landing.lead.toast.error"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        industry_other:
          form.industry === "Other" ? form.industry_other.trim() : null,
      };
      const { data } = await axios.post(`${API}/leads`, payload);
      setConfirmation(data);
      toast.success(t("landing.lead.toast.success"));
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Submission failed. Please try again or email us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
              <span className="inline-block h-px w-8 bg-slate-300" />
              <span>{t("landing.lead.eyebrow")}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] mt-1 text-slate-900 leading-[1.05]">
              {t("landing.lead.headline")}
            </h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">
              {t("landing.lead.body")}
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: Mail, text: t("landing.lead.benefit1") },
                { icon: Send, text: t("landing.lead.benefit2") },
                { icon: ShieldCheck, text: t("landing.lead.benefit3") },
              ].map((b) => (
                <div key={b.text} className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-md bg-[#0072CE]/10 text-[#0072CE] border border-[#0072CE]/15 flex items-center justify-center shrink-0">
                    <b.icon size={16} />
                  </div>
                  <span className="text-slate-700 text-sm md:text-base">
                    {b.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <form
            onSubmit={handleSubmit}
            data-testid="lead-form"
            className="lg:col-span-7 bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label htmlFor="company_name" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.company")} <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="company_name"
                  data-testid="lead-input-company-name"
                  value={form.company_name}
                  onChange={update("company_name")}
                  placeholder={t("landing.lead.placeholder.company")}
                  className="mt-1.5 bg-white border-slate-300 focus-visible:ring-[#0072CE]"
                />
                {errors.company_name && (
                  <p className="text-xs text-rose-600 mt-1">{errors.company_name}</p>
                )}
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.industry")} <span className="text-rose-600">*</span>
                </Label>
                <Select
                  value={form.industry}
                  onValueChange={(v) => update("industry")(v)}
                >
                  <SelectTrigger
                    data-testid="lead-select-industry"
                    className="mt-1.5 bg-white border-slate-300 focus:ring-[#0072CE]"
                  >
                    <SelectValue placeholder={t("landing.lead.placeholder.industry")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {INDUSTRIES.map((i) => (
                      <SelectItem
                        key={i}
                        value={i}
                        data-testid={`lead-industry-option-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.industry && (
                  <p className="text-xs text-rose-600 mt-1">{errors.industry}</p>
                )}
              </div>

              <div>
                <Label htmlFor="industry_other" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.industry_other")}
                </Label>
                <Input
                  id="industry_other"
                  data-testid="lead-input-industry-other"
                  value={form.industry_other}
                  onChange={update("industry_other")}
                  placeholder={t("landing.lead.placeholder.industry_other")}
                  disabled={form.industry !== "Other"}
                  className="mt-1.5 bg-white border-slate-300 disabled:bg-slate-100 disabled:text-slate-400"
                />
                {errors.industry_other && (
                  <p className="text-xs text-rose-600 mt-1">{errors.industry_other}</p>
                )}
              </div>

              <div>
                <Label htmlFor="first_name" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.first")} <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="first_name"
                  data-testid="lead-input-first-name"
                  value={form.first_name}
                  onChange={update("first_name")}
                  placeholder={t("landing.lead.placeholder.first")}
                  className="mt-1.5 bg-white border-slate-300"
                />
                {errors.first_name && (
                  <p className="text-xs text-rose-600 mt-1">{errors.first_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="last_name" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.last")} <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="last_name"
                  data-testid="lead-input-last-name"
                  value={form.last_name}
                  onChange={update("last_name")}
                  placeholder={t("landing.lead.placeholder.last")}
                  className="mt-1.5 bg-white border-slate-300"
                />
                {errors.last_name && (
                  <p className="text-xs text-rose-600 mt-1">{errors.last_name}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="business_email" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.email")} <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="business_email"
                  type="email"
                  data-testid="lead-input-business-email"
                  value={form.business_email}
                  onChange={update("business_email")}
                  placeholder={t("landing.lead.placeholder.email")}
                  className="mt-1.5 bg-white border-slate-300"
                />
                {errors.business_email && (
                  <p className="text-xs text-rose-600 mt-1">{errors.business_email}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.spend")} <span className="text-rose-600">*</span>
                </Label>
                <Select
                  value={form.annual_indirect_spend}
                  onValueChange={(v) => update("annual_indirect_spend")(v)}
                >
                  <SelectTrigger
                    data-testid="lead-select-spend"
                    className="mt-1.5 bg-white border-slate-300"
                  >
                    <SelectValue placeholder={t("landing.lead.placeholder.spend")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SPEND_RANGES.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        data-testid={`lead-spend-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.annual_indirect_spend && (
                  <p className="text-xs text-rose-600 mt-1">{errors.annual_indirect_spend}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="problem_statement" className="text-sm font-semibold text-slate-800">
                  {t("landing.lead.field.problem")} <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="problem_statement"
                  data-testid="lead-input-problem"
                  value={form.problem_statement}
                  onChange={update("problem_statement")}
                  placeholder={t("landing.lead.placeholder.problem")}
                  maxLength={300}
                  className="mt-1.5 bg-white border-slate-300"
                />
                <div className="mt-1 flex items-center justify-between">
                  {errors.problem_statement ? (
                    <p className="text-xs text-rose-600">{errors.problem_statement}</p>
                  ) : (
                    <span className="text-xs text-slate-500">
                      {t("landing.lead.problem_hint")}
                    </span>
                  )}
                  <span className="text-xs text-slate-400">
                    {form.problem_statement.length}/300
                  </span>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="lead-submit-button"
              disabled={submitting}
              className="w-full md:w-auto bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md px-7 py-6 text-base font-semibold"
            >
              {submitting ? t("landing.lead.btn_submitting") : t("landing.lead.btn_submit")}
            </Button>
            <p className="text-xs text-slate-500">
              {t("landing.lead.consent")}
            </p>
          </form>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={!!confirmation}
        onOpenChange={(o) => !o && setConfirmation(null)}
      >
        <DialogContent
          data-testid="lead-confirmation-dialog"
          className="max-w-lg"
        >
          <DialogHeader>
            <div className="h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 size={24} />
            </div>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {t("landing.lead.confirm.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {confirmation?.message}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
                {t("landing.lead.confirm.next")}
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                {confirmation?.actions_documented?.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2"
                    data-testid={`confirmation-action-${i}`}
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#0072CE] shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-slate-500">
              {t("landing.lead.confirm.ref")}{" "}
              <span className="font-mono text-slate-700">{confirmation?.id}</span>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
